import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { LayoutService } from '@app/core/services/layout.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { P2ReleaseNoteEditGet, P2ReleaseNoteService, P2ReleaseType } from '@app/services/p2-release-note.service';

@Component({
    selector: 'tt-p2-release-note-edit',
    templateUrl: './p2-release-note-edit.component.html',
    styleUrls: ['./p2-release-note-edit.component.css'],
    standalone: false,
})
export class P2ReleaseNoteEditComponent implements OnInit, OnChanges {
    @Input() public p2ReleaseNoteKeyno?: string;
    @Input() public inline = false;
    @Output() public onSave = new EventEmitter<void>();

    public edit?: P2ReleaseNoteEditGet;
    public releaseTypes: P2ReleaseType[] = [];
    public lockedSave = false;
    public readonly tabletSize: MediaQueryList;
    public readonly videoUrlButtons: FormButton[] = [{
        id: 'goto',
        icon: 'fas fa-window-restore',
        type: 'primary',
        onClick: () => {
            if (this.edit?.video_url) window.open(this.edit.video_url);
        },
    }];

    private navigator?: TTViewNavigator;

    constructor(
        private releaseNotes: P2ReleaseNoteService,
        layout: LayoutService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) navigator?: TTViewNavigator,
    ) {
        this.tabletSize = layout.getMediaQueries().tablet;
        this.navigator = useNavigatorFor(navigator, this);
    }

    public ngOnInit(): void {
        void this.load(this.p2ReleaseNoteKeyno ?? this.navigator?.params['p2_releasenote_keyno']);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['p2ReleaseNoteKeyno'] && !changes['p2ReleaseNoteKeyno'].firstChange) {
            void this.load(this.p2ReleaseNoteKeyno);
        }
    }

    public async saveChanges(): Promise<void> {
        if (!this.edit || this.lockedSave) return;

        this.lockedSave = true;
        try {
            await this.releaseNotes.saveReleaseNote(this.edit);
            if (this.tabletSize.matches && this.inline) {
                this.onSave.emit();
            } else {
                await this.navigator?.back();
            }
        } catch (error) {
            // Keep the editor open and unlock saving so the user can retry.
        } finally {
            this.lockedSave = false;
        }
    }

    private async load(keyno?: unknown): Promise<void> {
        if (keyno === undefined || keyno === null || `${keyno}` === '') return;

        const params = { p2_releasenote_keyno: `${keyno}` };
        const [edit, releaseTypes] = await Promise.all([
            this.releaseNotes.getReleaseNote(params),
            this.releaseNotes.listReleaseTypes(),
        ]);
        this.edit = edit;
        this.releaseTypes = releaseTypes;
    }
}
